import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../api/public';
import s from './LeadSetup.module.css';

interface LeadData {
  name: string;
  email: string;
  company_name: string;
  plan_name: string | null;
  price_monthly: number | null;
}

export function LeadSetup() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [slug, setSlug] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('トークンが指定されていません。メールのリンクを確認してください。');
      setLoading(false);
      return;
    }
    api.verifyLeadToken(token)
      .then((data) => {
        setLeadData(data);
        setLoading(false);
      })
      .catch((err) => {
        setTokenError(err instanceof Error ? err.message : '無効なトークンです。リンクの有効期限が切れている可能性があります。');
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.completeLeadSetup({ token, slug, password });
      setTenantSlug(res.tenant.slug);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'セットアップに失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.card}>
        <a href="/" className={s.logo}>
          <span className={s.logoBadge}>ME</span>
          <span className={s.logoText}>住縁 Mirai EMS</span>
        </a>

        {loading && <div className={s.loading}>読み込み中...</div>}

        {tokenError && <div className={s.error}>{tokenError}</div>}

        {success && (
          <div className={s.success}>
            <span className={s.successIcon}>&#10003;</span>
            <h3 className={s.successTitle}>セットアップが完了しました</h3>
            <p className={s.successDesc}>
              アカウントの準備が整いました。<br />
              以下のリンクからダッシュボードにアクセスしてください。
            </p>
            <a
              className={s.successLink}
              href={`https://${tenantSlug}.jiayin.jp/tenant/login`}
              target="_blank"
              rel="noopener noreferrer"
            >
              ダッシュボードを開く
            </a>
          </div>
        )}

        {!loading && !tokenError && !success && leadData && (
          <>
            <h2 className={s.title}>テナントセットアップ</h2>
            <p className={s.desc}>ご利用開始に必要な情報を設定してください。</p>

            <div className={s.infoRow}>
              <div className={s.infoItem}>
                <span className={s.infoLabel}>名前</span>
                <span className={s.infoValue}>{leadData.name}</span>
              </div>
              <div className={s.infoItem}>
                <span className={s.infoLabel}>メール</span>
                <span className={s.infoValue}>{leadData.email}</span>
              </div>
              <div className={s.infoItem}>
                <span className={s.infoLabel}>会社名</span>
                <span className={s.infoValue}>{leadData.company_name || '-'}</span>
              </div>
              <div className={s.infoItem}>
                <span className={s.infoLabel}>プラン</span>
                <span className={s.infoValue}>
                  {leadData.plan_name
                    ? `${leadData.plan_name}${leadData.price_monthly != null ? `（¥${leadData.price_monthly.toLocaleString('ja-JP')}/月）` : ''}`
                    : '未指定'}
                </span>
              </div>
            </div>

            {error && <div className={s.error}>{error}</div>}

            <form className={s.form} onSubmit={handleSubmit}>
              <div className={s.field}>
                <label className={s.label}>テナントID（英数字・ハイフン）</label>
                <input
                  className={s.input}
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                  placeholder="my-company"
                  required
                  autoFocus
                />
              </div>
              <div className={s.field}>
                <label className={s.label}>パスワード</label>
                <input
                  className={s.input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <span className={s.passwordRules}>8文字以上</span>
              </div>
              <div className={s.field}>
                <label className={s.label}>パスワード（確認）</label>
                <input
                  className={s.input}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <button className={s.btn} type="submit" disabled={submitting}>
                {submitting ? 'セットアップ中...' : 'セットアップを完了する'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
